import { useCallback, useEffect, useState } from "react";

import { useApi } from "@platform/hooks/useApi";

import type {
  ManagedResourceUI,
  CreateDatabaseRequest,
  CreateCacheRequest,
  CreateQueueRequest,
} from "../types";

interface UseProjectResourcesReturn {
  resources: ManagedResourceUI[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Fetch all managed resources for a project. */
export function useProjectResources(
  projectAlias: string | undefined,
): UseProjectResourcesReturn {
  const api = useApi();
  const [resources, setResources] = useState<ManagedResourceUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<ManagedResourceUI[]>(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/resources`,
        {
          errorMessage: `Failed to load resources for project: ${projectAlias}`,
          showLoading: false,
        },
      );
      setResources(Array.isArray(result) ? result : []);
    } catch {
      setError("Failed to load resources.");
    } finally {
      setLoading(false);
    }
    // `api` is intentionally excluded: useApi() returns a new object every
    // render, which would make this callback change identity every render
    // and re-trigger the fetch effect infinitely. The api.get method is
    // internally memoized and stable. Matches useStacksByProject pattern.
  }, [projectAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { resources, loading, error, reload };
}

interface UseCreateResourceReturn {
  createDatabase: (
    projectAlias: string,
    dto: CreateDatabaseRequest,
  ) => Promise<ManagedResourceUI | null>;
  createCache: (
    projectAlias: string,
    dto: CreateCacheRequest,
  ) => Promise<ManagedResourceUI | null>;
  createQueue: (
    projectAlias: string,
    dto: CreateQueueRequest,
  ) => Promise<ManagedResourceUI | null>;
  isSubmitting: boolean;
  submitError: string | null;
  resetError: () => void;
}

/** Mutation hook for creating managed resources. */
export function useCreateResource(): UseCreateResourceReturn {
  const api = useApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const doPost = useCallback(
    async <T>(path: string, body: T): Promise<ManagedResourceUI | null> => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const result = await api.post<ManagedResourceUI, T>(path, body, {
          errorMessage: "Failed to create resource",
        });
        return result;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to create resource";
        setSubmitError(msg);
        return null;
      } finally {
        setIsSubmitting(false);
      }
      // `api` excluded — see useProjectResources note. api.post is stable.
    },
    [],
  );

  const createDatabase = useCallback(
    (projectAlias: string, dto: CreateDatabaseRequest) =>
      doPost(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/resources/database`,
        dto,
      ),
    [doPost],
  );

  const createCache = useCallback(
    (projectAlias: string, dto: CreateCacheRequest) =>
      doPost(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/resources/cache`,
        dto,
      ),
    [doPost],
  );

  const createQueue = useCallback(
    (projectAlias: string, dto: CreateQueueRequest) =>
      doPost(
        `/api/v1/projects/${encodeURIComponent(projectAlias)}/resources/queue`,
        dto,
      ),
    [doPost],
  );

  const resetError = useCallback(() => setSubmitError(null), []);

  return {
    createDatabase,
    createCache,
    createQueue,
    isSubmitting,
    submitError,
    resetError,
  };
}

interface UseResourceActionsReturn {
  stop: (alias: string) => Promise<ManagedResourceUI | null>;
  restart: (alias: string) => Promise<ManagedResourceUI | null>;
  retry: (alias: string) => Promise<ManagedResourceUI | null>;
  deprovision: (alias: string) => Promise<boolean>;
  isActing: boolean;
}

/** Hook for stop/restart/retry/deprovision actions on a resource. */
export function useResourceActions(): UseResourceActionsReturn {
  const api = useApi();
  const [isActing, setIsActing] = useState(false);

  const stop = useCallback(
    async (alias: string): Promise<ManagedResourceUI | null> => {
      setIsActing(true);
      try {
        return await api.patch<ManagedResourceUI>(
          `/api/v1/resources/${encodeURIComponent(alias)}/stop`,
          {},
          {
            errorMessage: `Failed to stop resource: ${alias}`,
            showLoading: false,
          },
        );
      } finally {
        setIsActing(false);
      }
      // `api` excluded — see useProjectResources note. api.patch is stable.
    },
    [],
  );

  const restart = useCallback(
    async (alias: string): Promise<ManagedResourceUI | null> => {
      setIsActing(true);
      try {
        return await api.patch<ManagedResourceUI>(
          `/api/v1/resources/${encodeURIComponent(alias)}/restart`,
          {},
          {
            errorMessage: `Failed to restart resource: ${alias}`,
            showLoading: false,
          },
        );
      } finally {
        setIsActing(false);
      }
      // `api` excluded — see useProjectResources note. api.patch is stable.
    },
    [],
  );

  const retry = useCallback(
    async (alias: string): Promise<ManagedResourceUI | null> => {
      setIsActing(true);
      try {
        return await api.patch<ManagedResourceUI>(
          `/api/v1/resources/${encodeURIComponent(alias)}/retry`,
          {},
          {
            errorMessage: `Failed to retry provisioning: ${alias}`,
            showLoading: false,
          },
        );
      } catch {
        return null;
      } finally {
        setIsActing(false);
      }
      // `api` excluded — see useProjectResources note. api.patch is stable.
    },
    [],
  );

  const deprovision = useCallback(async (alias: string): Promise<boolean> => {
    setIsActing(true);
    try {
      await api.delete(`/api/v1/resources/${encodeURIComponent(alias)}`, {
        errorMessage: `Failed to deprovision resource: ${alias}`,
        showLoading: false,
      });
      return true;
    } catch {
      return false;
    } finally {
      setIsActing(false);
    }
    // `api` excluded — see useProjectResources note. api.delete is stable.
  }, []);

  return { stop, restart, retry, deprovision, isActing };
}
